import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const C = {
  navy:         '#0f172a',
  border:       'rgba(255,255,255,0.07)',
  blue:         '#3b82f6',
  blueActive:   'rgba(59,130,246,0.12)',
  textActive:   '#f8fafc',
  textInactive: '#94a3b8',
  textMuted:    '#475569',
};

const NAV = [
  { label: 'Main', items: [
    { to: '/dashboard', icon: GridIcon,     label: 'Dashboard' },
    { to: '/matcher',   icon: SearchIcon,   label: 'School Matcher' },
    { to: '/schools',   icon: SchoolIcon,   label: 'Build My List' },
  ]},
  { label: 'Tools', items: [
    { to: '/personal-statement', icon: PSIcon,      label: 'Personal Statement' },
    { to: '/checklist',          icon: CheckIcon,   label: 'Checklists' },
    { to: '/essays',             icon: PenIcon,     label: 'Essay Prompts' },
    { to: '/deadlines',          icon: CalIcon,     label: 'Deadlines' },
  ]},
  { label: 'Account', items: [
    { to: '/settings',  icon: SettingsIcon, label: 'Settings' },
  ]},
];

export default function Sidebar({ session }) {
  const location = useLocation();
  const navigate  = useNavigate();

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  const userName = session?.user?.user_metadata?.full_name || session?.user?.email || 'You';
  const initials = userName.charAt(0).toUpperCase();

  return (
    <aside style={{ width: 'var(--sidebar-width)', flexShrink: 0, background: C.navy, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', padding: '20px 0', position: 'sticky', top: 0, height: '100vh' }}>

      {/* Logo */}
      <Link to="/dashboard" style={{ padding: '0 16px 28px', display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
        <div style={{ width: 30, height: 30, background: C.blue, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
        </div>
        <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.3px' }}>
          Pre<span style={{ color: '#60a5fa' }}>Visr</span>
        </span>
      </Link>

      {/* Nav groups */}
      {NAV.map(function(group) {
        return (
          <div key={group.label} style={{ padding: '0 8px', marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.textMuted, padding: '0 8px', margin: '8px 0 3px' }}>
              {group.label}
            </div>
            {group.items.map(function(item) {
              const active = location.pathname === item.to || (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
              return (
                <Link key={item.to} to={item.to}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 500, color: active ? C.textActive : C.textInactive, background: active ? C.blueActive : 'transparent', borderLeft: active ? `2px solid ${C.blue}` : '2px solid transparent', transition: 'all .12s', textDecoration: 'none', marginBottom: 1 }}
                  onMouseEnter={function(e) { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#cbd5e1'; }}}
                  onMouseLeave={function(e) { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textInactive; }}}>
                  <item.icon active={active} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        );
      })}

      {/* Resources */}
      <div style={{ padding: '0 8px', marginBottom: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.textMuted, padding: '0 8px', margin: '8px 0 3px' }}>Resources</div>
        {[{ to: '/guide', label: 'Med School Guide' }, { to: '/about', label: 'About PreVisr' }].map(function(item) {
          return (
            <Link key={item.to} to={item.to}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 500, color: C.textMuted, textDecoration: 'none', transition: 'all .12s', marginBottom: 1 }}
              onMouseEnter={function(e) { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = 'transparent'; }}>
              {item.label}
            </Link>
          );
        })}
      </div>

      {/* User row */}
      <div style={{ marginTop: 'auto', padding: '12px 8px 0', borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 7 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName.split(' ')[0]}</div>
            <div style={{ fontSize: 10, color: C.textMuted }}>Free plan</div>
          </div>
          <button onClick={handleLogout} title="Log out"
            style={{ background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, borderRadius: 5, display: 'flex', alignItems: 'center', transition: 'color .12s' }}
            onMouseEnter={function(e) { e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={function(e) { e.currentTarget.style.color = C.textMuted; }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}

function PSIcon({ active })       { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>; }
function GridIcon({ active })     { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>; }
function SearchIcon({ active })   { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function SchoolIcon({ active })   { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>; }
function CheckIcon({ active })    { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><polyline points="9,11 12,14 22,4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>; }
function PenIcon({ active })      { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>; }
function CalIcon({ active })      { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>; }
function SettingsIcon({ active }) { return <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={active ? '#60a5fa' : '#64748b'} strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>; }
